// src/components/Common/ImageUpload.jsx
import React, { useEffect, useState } from 'react';
import { Upload, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ImageUpload = ({
  onImageUpload,
  existingImage = '',
  label = 'Product Image *',
  multiple = false,
  disabled = false,
  className = '',
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingImage);
  const [progress, setProgress] = useState(0);

  // Keep preview in sync when the parent changes the existing image
  useEffect(() => {
    setPreview(existingImage);
  }, [existingImage]);

  // Safe function call
  const safeImageUpload = (url) => {
    if (typeof onImageUpload === 'function') {
      onImageUpload(url);
    } else {
      console.error('❌ onImageUpload is not a function!');
    }
  };

  const uploadSingleFile = async (file) => {
    // Validate file
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File size too large! Maximum size is 10MB');
    }

    if (file.size === 0) {
      throw new Error('The selected file is empty (0 bytes). Please choose a different image.');
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type! Please upload JPG, PNG, or WebP images.');
    }

    // Show local preview for single uploads
    if (!multiple) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }

    // Upload through backend
    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Get auth headers
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = {
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload to your backend
      const response = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();

      clearInterval(progressInterval);
      setProgress(100);

      if (response.ok && data.success) {
        return data.url;
      }
      throw new Error(data.error || `Upload failed (HTTP ${response.status})`);
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    event.target.value = ''; // Allow re-selecting the same file

    const uploaded = [];
    for (const file of files) {
      try {
        const url = await uploadSingleFile(file);
        uploaded.push(url);
        safeImageUpload(url);
      } catch (error) {
        console.error('Upload error:', error);
        alert(error.message || `Upload failed: ${error.response?.data?.error || error.message}`);
        if (!multiple) setPreview('');
        break;
      }
    }

    if (uploaded.length > 0) {
      const msg = multiple
        ? `${uploaded.length} image${uploaded.length > 1 ? 's' : ''} uploaded successfully!`
        : 'Image uploaded successfully!';
      setTimeout(() => alert(msg), 300);
    }
  };

  const handleRemove = () => {
    setPreview('');
    safeImageUpload('');
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      <div className="space-y-4">
        {/* Preview */}
        {preview && !multiple && (
          <div className="relative inline-block">
            <img
              src={preview}
              alt="Preview"
              className={`w-48 h-48 object-cover rounded-lg border-2 border-gray-200 ${className}`}
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-lg"
              disabled={uploading}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Upload section */}
        <div className="space-y-2">
          <label className={`cursor-pointer inline-block w-full ${(uploading || disabled) && 'opacity-50 pointer-events-none'}`}>
            <div className={`p-4 border-2 border-dashed rounded-lg transition-all duration-300 ${
              uploading
                ? 'border-blue-400 bg-blue-50'
                : preview
                ? 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
            }`}>
              <div className="flex flex-col items-center justify-center space-y-2">
                {uploading ? (
                  <>
                    <div className="relative w-12 h-12">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 border-4 border-blue-200 rounded-full"></div>
                        <div
                          className="absolute w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"
                          style={{ transform: `rotate(${progress * 3.6}deg)` }}
                        ></div>
                      </div>
                      <span className="absolute -bottom-6 text-sm font-medium text-blue-600">
                        {progress}%
                      </span>
                    </div>
                    <span className="text-sm text-gray-700">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-500" />
                    <div className="text-center">
                      <span className="text-sm font-medium text-gray-700 block">
                        {preview ? 'Change Image' : multiple ? 'Upload Images' : 'Upload Product Image'}
                      </span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        {multiple ? 'Select one or more images' : 'Click to browse or drag and drop'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading || disabled}
              multiple={multiple}
            />
          </label>

          {/* Progress bar */}
          {uploading && progress > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500 space-y-1">
        <p>• Supports JPG, PNG, WebP, GIF formats</p>
        <p>• Maximum file size: 10MB</p>
        <p>• Recommended size: 800x800 pixels</p>
        <p className="text-blue-600 font-medium">✓ Secure upload through server</p>
      </div>
    </div>
  );
};

export default ImageUpload;
